<template>
  <div class="flex flex-col gap-6">
    <!-- ============ ส่วนที่ 1: ระดับครัวเรือน ============ -->
    <CardLayout headerText="ปัญหาและสวัสดิการ ระดับครัวเรือน" :showHeaderButton="false">
      <div v-if="loading" class="py-8 text-center text-theme-37">
        <LoadingIcon icon="oval" class="w-8 h-8 mx-auto" /> กำลังวิเคราะห์ปัญหาและสวัสดิการ…
      </div>
      <div v-else-if="error" class="py-6 text-center">
        <div class="text-theme-24">{{ error }}</div>
        <button class="btn btn-outline-secondary mt-3" @click="fetchRecommendations">ลองใหม่</button>
      </div>
      <div v-else-if="data">
        <div v-if="data.survey_status === 'DRAFT'"
             class="flex items-center gap-2 rounded-md px-3 py-2 mb-4 text-sm" style="background:#fdf3df; color:#B36B00">
          <AlertCircleIcon class="w-4 h-4 flex-shrink-0" /> แบบสำรวจยังเป็นฉบับร่าง ผลวิเคราะห์อาจยังไม่ครบ
        </div>

        <!-- KPI tiles -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div class="rounded-md border border-gray-200 p-3 text-center" style="border-top:3px solid #CE3131">
            <div class="text-3xl font-semibold" style="color:#CE3131">{{ summary.problems_total }}</div>
            <div class="text-sm text-gray-700 mt-1">ปัญหาระดับครัวเรือน</div>
            <div class="text-xs text-theme-37 mt-0.5">เร่งด่วน {{ summary.problems_urgent }} · ติดตาม {{ summary.problems_monitor }}</div>
          </div>
          <div class="rounded-md border border-gray-200 p-3 text-center" style="border-top:3px solid #F78B00">
            <div class="text-3xl font-semibold" style="color:#F78B00">{{ summary.welfare_recommended }}</div>
            <div class="text-sm text-gray-700 mt-1">สวัสดิการแนะนำให้ยื่นขอ</div>
            <div class="text-xs text-theme-37 mt-0.5">รายบุคคลอีก {{ summary.member_welfare_recommended }} รายการ</div>
          </div>
          <div class="rounded-md border border-gray-200 p-3 text-center" style="border-top:3px solid #42B983">
            <div class="text-3xl font-semibold" style="color:#42B983">{{ summary.welfare_received }}</div>
            <div class="text-sm text-gray-700 mt-1">ได้รับสวัสดิการแล้ว</div>
            <div class="text-xs text-theme-37 mt-0.5">ตามที่ครัวเรือนแจ้ง</div>
          </div>
          <div class="rounded-md border border-gray-200 p-3 text-center" style="border-top:3px solid #415A77">
            <div class="text-3xl font-semibold" style="color:#415A77">
              {{ summary.members_with_problems }}<span class="text-lg text-theme-37">/{{ summary.member_total }}</span>
            </div>
            <div class="text-sm text-gray-700 mt-1">สมาชิกที่พบปัญหา</div>
            <div class="text-xs text-theme-37 mt-0.5">รวม {{ summary.member_problems_total }} ปัญหารายบุคคล</div>
          </div>
        </div>

        <!-- household problems -->
        <section>
          <div class="flex items-center gap-2 mb-3">
            <AlertTriangleIcon class="w-4 h-4 flex-shrink-0" style="color:#CE3131" />
            <div class="text-sm font-semibold" style="color:#a12424">ปัญหาที่ตรวจพบ ({{ problems.length }})</div>
          </div>
          <div v-if="!problems.length" class="flex items-center gap-2 text-sm text-theme-42">
            <CheckCircleIcon class="w-4 h-4 flex-shrink-0" /> ไม่พบปัญหาระดับครัวเรือน
          </div>
          <div v-else class="flex flex-col gap-2">
            <div v-for="p in problems" :key="p.code"
                 class="rounded-md border bg-white p-3"
                 :style="{ borderLeft: '3px solid ' + sevColor(p.severity) }">
              <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span class="text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap" :style="chipStyle(p.severity)">{{ sevLabel(p.severity) }}</span>
                <span class="font-medium text-gray-800 break-words">{{ p.title_th }}</span>
                <span v-if="p.dimension" class="text-xs text-theme-37 bg-gray-100 px-2 py-0.5 rounded whitespace-nowrap">มิติ{{ p.dimension }}</span>
              </div>
              <div v-if="p.evidence_th" class="text-sm text-gray-700 mt-1.5 max-w-3xl break-words">{{ p.evidence_th }}</div>
              <div v-if="linkedWelfare(p).length" class="flex items-start gap-1.5 text-sm mt-2 max-w-3xl">
                <GiftIcon class="w-3.5 h-3.5 flex-shrink-0 mt-1" style="color:#1f8f5b" />
                <span class="break-words">
                  <span class="text-theme-37">สวัสดิการที่ช่วยได้: </span>
                  <span v-for="(w, i) in linkedWelfare(p)" :key="w.welfare_id" class="text-gray-800">{{ w.name_th }}<span v-if="w.status === 'received'" class="text-theme-42"> (ได้รับแล้ว)</span><span v-if="i < linkedWelfare(p).length - 1">, </span></span>
                </span>
              </div>
              <template v-if="p.rationale_th">
                <button type="button" class="inline-flex items-center gap-1 text-xs font-medium mt-2" style="color:#415A77"
                        @click="toggle('p:' + p.code)">
                  <ChevronDownIcon class="w-3.5 h-3.5 transform transition-transform" :class="{ 'rotate-180': expanded['p:' + p.code] }" />
                  {{ expanded['p:' + p.code] ? 'ซ่อนเหตุผลและเกณฑ์' : 'ดูเหตุผลและเกณฑ์' }}
                </button>
                <div v-if="expanded['p:' + p.code]" class="text-sm text-theme-37 mt-1 max-w-3xl break-words">{{ p.rationale_th }}</div>
              </template>
            </div>
          </div>
        </section>

        <!-- household welfare: to apply for, grouped by the problem/condition behind it -->
        <section class="border-t border-gray-200 mt-6 pt-5">
          <div class="flex items-center gap-2 mb-3">
            <GiftIcon class="w-4 h-4 flex-shrink-0" style="color:#1f8f5b" />
            <div class="text-sm font-semibold" style="color:#1f6b45">สวัสดิการแนะนำให้ยื่นขอ ({{ recommendedCount }})</div>
          </div>
          <div v-if="!welfareGroups.length" class="text-sm text-theme-37">ไม่มีสวัสดิการที่ต้องแนะนำเพิ่ม</div>
          <div v-else class="flex flex-col gap-4">
            <div v-for="g in welfareGroups" :key="g.key">
              <div class="flex items-start gap-2 mb-2 max-w-3xl">
                <span class="w-2 h-2 rounded-full flex-shrink-0 mt-2"
                      :style="{ background: g.problem ? sevColor(g.problem.severity) : '#42B983' }"></span>
                <div class="text-sm break-words">
                  <span class="text-theme-37">{{ g.problem ? 'แก้ปัญหา: ' : 'เข้าเงื่อนไข: ' }}</span>
                  <span class="font-medium text-gray-800">{{ g.reason }}</span>
                </div>
              </div>
              <template v-if="!g.problem && g.rationale">
                <button type="button" class="inline-flex items-center gap-1 text-xs font-medium mb-2 ml-4" style="color:#415A77"
                        @click="toggle('g:' + g.key)">
                  <ChevronDownIcon class="w-3.5 h-3.5 transform transition-transform" :class="{ 'rotate-180': expanded['g:' + g.key] }" />
                  {{ expanded['g:' + g.key] ? 'ซ่อนเหตุผล' : 'ดูเหตุผล' }}
                </button>
                <div v-if="expanded['g:' + g.key]" class="text-sm text-theme-37 mb-2 ml-4 max-w-3xl break-words">{{ g.rationale }}</div>
              </template>
              <div class="flex flex-col gap-2 ml-4">
                <div v-for="w in g.items" :key="w.welfare_id"
                     class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-md border border-gray-200 bg-white p-3">
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-gray-800 break-words">{{ w.name_th }}</div>
                    <div v-if="w.agency" class="text-sm text-gray-700 break-words mt-0.5">
                      <span class="text-theme-37">หน่วยงาน: </span>{{ w.agency }}
                    </div>
                  </div>
                  <a v-if="w.url" :href="w.url" target="_blank" rel="noopener noreferrer"
                     class="inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap self-start sm:self-auto flex-shrink-0"
                     style="color:#415A77; background:#eef1f6; border:1px solid #dce1ea">
                    เว็บไซต์หน่วยงาน <ExternalLinkIcon class="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- household welfare: already received -->
        <section class="border-t border-gray-200 mt-6 pt-5">
          <div class="flex items-center gap-2 mb-3">
            <CheckCircleIcon class="w-4 h-4 flex-shrink-0" style="color:#42B983" />
            <div class="text-sm font-semibold" style="color:#1f6b45">ได้รับสวัสดิการแล้ว ({{ received.length }})</div>
          </div>
          <div v-if="!received.length" class="text-sm text-theme-37">ครัวเรือนยังไม่ได้แจ้งว่าได้รับสวัสดิการใด</div>
          <div v-else class="rounded-md border border-gray-200 bg-white divide-y divide-gray-200">
            <div v-for="w in received" :key="w.welfare_id"
                 class="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-3 py-2">
              <div class="flex-1 min-w-0 text-sm break-words">
                <span class="font-medium text-gray-800">{{ w.name_th }}</span>
                <span v-if="w.agency" class="text-theme-37"> · {{ w.agency }}</span>
              </div>
              <a v-if="w.url" :href="w.url" target="_blank" rel="noopener noreferrer"
                 class="inline-flex items-center gap-1 text-xs font-medium whitespace-nowrap self-start sm:self-auto" style="color:#415A77">
                เว็บไซต์หน่วยงาน <ExternalLinkIcon class="w-3 h-3" />
              </a>
            </div>
          </div>
        </section>

        <div v-if="computedAt" class="text-xs text-theme-37 text-right mt-5">วิเคราะห์เมื่อ {{ computedAt }}</div>
      </div>
    </CardLayout>

    <!-- ============ ส่วนที่ 2: รายบุคคล ============ -->
    <CardLayout v-if="data && data.members && data.members.length"
                headerText="ปัญหาและสวัสดิการ รายบุคคล" :showHeaderButton="false">
      <div class="flex flex-col gap-3">
        <div v-for="(m, mi) in data.members" :key="m.member_id || mi" class="rounded-md border border-gray-200 p-4">
          <div class="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <span class="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                    style="background:#415A77">{{ (m.name_th || '?').trim().charAt(0) }}</span>
              <div class="flex-1 min-w-0">
                <div class="font-medium text-gray-800 break-words">{{ m.name_th }}</div>
                <div class="text-xs text-theme-37 break-words">
                  <span v-if="m.age != null">{{ m.age }} ปี · </span>
                  <span v-if="m.gender">{{ m.gender }} · </span>{{ relationLabel(m.relation_th) }}
                </div>
              </div>
            </div>
            <span v-if="m.problem_count" class="text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap self-start sm:self-auto flex-shrink-0"
                  :style="chipStyle('urgent')">พบปัญหา {{ m.problem_count }} รายการ</span>
            <span v-else class="text-xs font-medium text-theme-42 self-start sm:self-auto">ไม่พบปัญหา</span>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-if="m.problems.length" class="rounded-lg p-3" style="background:#fdf5f5; border:1px solid #f5d9d9">
              <div class="flex items-center gap-2 mb-2">
                <AlertTriangleIcon class="w-3.5 h-3.5 flex-shrink-0" style="color:#CE3131" />
                <div class="text-xs font-semibold" style="color:#a12424">ปัญหาที่พบ</div>
              </div>
              <div v-for="(p, i) in m.problems" :key="i"
                   class="flex items-start gap-2 text-sm border rounded-md px-3 py-2 mb-2 bg-white">
                <span class="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" :style="{ background: sevColor(p.severity) }"></span>
                <span class="flex-1 min-w-0 break-words">{{ p.title_th }}</span>
              </div>
            </div>
            <div v-if="m.welfare.length" class="rounded-lg p-3" style="background:#f4f9f6; border:1px solid #d9ecdf">
              <div class="flex items-center gap-2 mb-2">
                <GiftIcon class="w-3.5 h-3.5 flex-shrink-0" style="color:#1f8f5b" />
                <div class="text-xs font-semibold" style="color:#1f6b45">สวัสดิการที่แนะนำ</div>
              </div>
              <div v-for="(w, wi) in m.welfare" :key="w.welfare_id || wi"
                   class="flex items-start gap-2 text-sm border border-gray-200 rounded-md px-3 py-2 mb-2 bg-white">
                <span class="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" :style="{ background: statusDot(w.status) }"></span>
                <span class="flex-1 min-w-0 break-words">{{ w.name_th }}<span v-if="w.status === 'received'" class="text-theme-42"> (ได้รับแล้ว)</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CardLayout>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import axios from "axios";
import { useStore } from "vuex";
import CardLayout from "@/components/profile/layout/CardLayout.vue";

const props = defineProps({
  hhid: { type: String, required: true },
  surveyid: { type: String, required: true },
});

const store = useStore();
const data = ref(null);
const loading = ref(false);
const error = ref("");
const expanded = reactive({});
const toggle = (key) => { expanded[key] = !expanded[key]; };

// จัดกลุ่ม severity ให้เหลือ 2 ระดับ: เร่งด่วน vs ติดตาม
// เพื่อไม่ให้ค่าที่ backend อาจส่งมานอกเหนือ urgent/monitor (เช่น critical/high) ถูกป้ายผิด
const URGENT_LEVELS = ["urgent", "critical", "high", "severe"];
const isUrgent = (s) => URGENT_LEVELS.includes(String(s || "").toLowerCase());
const sevColor = (s) => (isUrgent(s) ? "#CE3131" : "#F78B00");
const sevLabel = (s) => (isUrgent(s) ? "แก้ไขเร่งด่วน" : "ติดตามการช่วยเหลือ");
const chipStyle = (s) =>
  isUrgent(s)
    ? { color: "#CE3131", background: "#fcebeb", border: "1px solid #f2c9c9" }
    : { color: "#B36B00", background: "#fdf3df", border: "1px solid #f0dcae" };
const statusDot = (s) => (s === "received" ? "#42B983" : "#F78B00");

// เผื่อกรณี backend ส่งค่าความสัมพันธ์เป็นคำศัพท์ภาษาอังกฤษดิบ ๆ (เช่น "MEMBER") มาแทนคำแปลไทย
const RELATION_LABEL_TH = { member: "สมาชิกในครัวเรือน", head: "หัวหน้าครัวเรือน" };
const relationLabel = (relationTh) => {
  const key = String(relationTh || "").trim().toLowerCase();
  return RELATION_LABEL_TH[key] || relationTh;
};

// ปัญหาเร่งด่วนขึ้นก่อน (sort แบบ stable คงลำดับเดิมภายในระดับเดียวกัน)
const problems = computed(() =>
  (data.value?.household?.problems || [])
    .map((p, i) => ({ p, i }))
    .sort((a, b) => (isUrgent(b.p.severity) - isUrgent(a.p.severity)) || a.i - b.i)
    .map(({ p }) => p)
);
const householdWelfare = computed(() => data.value?.household?.welfare || []);
const received = computed(() => householdWelfare.value.filter((w) => w.status === "received"));
const recommendedCount = computed(() => householdWelfare.value.filter((w) => w.status !== "received").length);

const linkedWelfare = (p) =>
  (p.welfare_ids || []).map((id) => householdWelfare.value.find((w) => w.welfare_id === id)).filter(Boolean);

// จัดกลุ่มสวัสดิการที่แนะนำตามปัญหา/เงื่อนไขที่ทำให้แนะนำ เพื่อไม่ให้เหตุผลเดียวกันแสดงซ้ำทุกรายการ
// กลุ่มที่มาจากปัญหาเรียงตามลำดับปัญหา (เร่งด่วนก่อน) แล้วตามด้วยกลุ่มที่มาจากเงื่อนไขสิทธิ์
const welfareGroups = computed(() => {
  const problemByCode = Object.fromEntries(problems.value.map((p) => [p.code, p]));
  const groups = new Map();
  for (const w of householdWelfare.value) {
    if (w.status === "received") continue;
    const problem = (w.problem_codes || []).map((c) => problemByCode[c]).find(Boolean);
    const key = problem ? problem.code : (w.rule_ids && w.rule_ids[0]) || w.reason_th || w.welfare_id;
    if (!groups.has(key)) {
      groups.set(key, { key, problem, reason: problem ? problem.title_th : w.reason_th, rationale: w.rationale_th, items: [] });
    }
    groups.get(key).items.push(w);
  }
  const order = (g) => (g.problem ? problems.value.indexOf(g.problem) : problems.value.length);
  return [...groups.values()].map((g, i) => ({ g, i })).sort((a, b) => order(a.g) - order(b.g) || a.i - b.i).map(({ g }) => g);
});

// ตัวเลขสรุป — ใช้ค่าจาก API ถ้ามี ไม่งั้นคำนวณจากรายการ (รองรับ API เวอร์ชันก่อนหน้า)
const summary = computed(() => {
  const s = data.value?.summary || {};
  const members = data.value?.members || [];
  const count = (arr, fn) => arr.reduce((n, x) => n + fn(x), 0);
  return {
    problems_total: s.problems_total ?? problems.value.length,
    problems_urgent: s.problems_urgent ?? problems.value.filter((p) => isUrgent(p.severity)).length,
    problems_monitor: s.problems_monitor ?? problems.value.filter((p) => !isUrgent(p.severity)).length,
    welfare_recommended: s.welfare_recommended ?? recommendedCount.value,
    welfare_received: s.welfare_received ?? received.value.length,
    member_total: s.member_total ?? members.length,
    members_with_problems: s.members_with_problems ?? members.filter((m) => m.problems.length).length,
    member_problems_total: s.member_problems_total ?? count(members, (m) => m.problems.length),
    member_welfare_recommended: s.member_welfare_recommended ?? count(members, (m) => m.welfare.filter((w) => w.status !== "received").length),
  };
});

const computedAt = computed(() => {
  const t = data.value?.computed_at;
  if (!t) return "";
  return new Date(t).toLocaleString("th-TH", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
});

async function fetchRecommendations() {
  if (!props.hhid || !props.surveyid) return;
  loading.value = true;
  error.value = "";
  try {
    const token = store.getters["userAuth/getToken"];
    const url = `${process.env.VUE_APP_BASE_API}house/${props.hhid}/${props.surveyid}/recommendations`;
    const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
    if (res.data && res.data.error) {
      error.value = res.data.message || "ไม่สามารถวิเคราะห์ได้";
      data.value = null;
    } else {
      data.value = res.data;
    }
  } catch (e) {
    error.value = "ไม่สามารถโหลดข้อมูลปัญหาและสวัสดิการได้";
  } finally {
    loading.value = false;
  }
}

onMounted(fetchRecommendations);
watch(() => [props.hhid, props.surveyid], fetchRecommendations);
</script>
